/** Anatomical adapter for the unmodified Hero Forge numerical animation.
 * Source arm L -> target arm R; source stage +X -> target forward +Z. */
import type {EquipmentWeaponId} from '../../shared/equipment';
import {sourceWeapon,weaponMotion,type ArmedAction} from '../../shared/weapon-motion';
import {B,createPose,type Pose as SourcePose} from '../../shared/fable-motion/rig';
import {starfall,thousandCuts} from '../../shared/fable-motion/supers';
import {referenceStrike} from '../../shared/fable-motion/animation';
import {isOriginalSkill,originalSkill,type SkillEvents} from '../../shared/fable-motion/skills';
import {weaponGrip} from './weapons';
import type {ClipEvents} from '../../shared/fable-motion/animation';
import {qRotate,normalize} from '../../shared/fable-motion/math';
import {boneTip,evaluate,qEuler,qMul,type Pose,type Quat} from './pose';
import type {Skeleton,V3} from './rig';

type Reach=(side:'R'|'L',wrist:V3,shaft:V3,pole:V3)=>void;
const mirrored=([x,y,z,w]:Quat):Quat=>[x,-y,-z,w];
const mirror=([x,y,z]:V3):V3=>[-x,y,z];
const stage=([x,y,z]:V3):V3=>[z,y,x];
const turn=qEuler(0,Math.PI/2,0);
const dash=1.4;
const smooth=(v:number)=>{const x=Math.max(0,Math.min(1,v));return x*x*(3-2*x);};
export function retargetFable(sk:Skeleton,pose:Pose,time:number,weapon:EquipmentWeaponId,action:ArmedAction,reach:Reach):number {
 const source=createPose(),cfg=weaponMotion(weapon,action),t=Math.max(0,Math.min(time,cfg.duration-1e-7)),superClip=action!=='strike';
 const bi=(name:string)=>sk.byName.get(name)!;
 const length=(name:string)=>{const i=bi(name),b=sk.bones[i];return Math.hypot(...b.tail.map((v,k)=>v-b.head[k]))*pose.bones[i].length;};
 let events:ClipEvents;
 if(isOriginalSkill(action))events=originalSkill(action,source,t,{weapon:sourceWeapon(weapon),dashDistance:dash,legs:[length('thighR'),length('shinR')]});
 else if(superClip)events=(action==='starfall'?starfall:thousandCuts)(source,t,{weapon:sourceWeapon(weapon),dashDistance:dash,legs:[length('thighR'),length('shinR')]});
 else events=referenceStrike(source,t/(weapon==='hammer'?1.875:1.25),weapon==='daggers'?'sword':weapon);
 const root=superClip?qMul(turn,mirrored(source.rot[B.root])):mirrored(source.rot[B.root]);
 pose.bones[bi('pelvis')].rot=qMul(root,mirrored(source.rot[B.hips]));
 for(const name of ['chest','head'] as const)pose.bones[bi(name)].rot=mirrored(source.rot[B[name]]);
 for(const target of ['R','L'] as const){const from=target==='R'?'L':'R';for(const part of ['thigh','shin','foot'] as const)pose.bones[bi(part+target)].rot=mirrored(source.rot[B[(part+from) as keyof typeof B]]);}
 // Longer legs do not inherit a slim pelvis' radial scale. Ease only the
 // staff-plant knee flex in that proportion regime: otherwise the knee swings
 // out of a short tunic whose waist correctly follows the narrower pelvis.
 // The authored clock, foot orientation and wide/neutral recipes stay intact.
 if(action==='tempest'&&(events as SkillEvents).groundWeapon){
  const slimLong=smooth((pose.bones[bi('thighR')].length/pose.bones[bi('pelvis')].thick-1)/.3);
  const ease=.3*slimLong*(events as SkillEvents).groundWeapon!;
  for(const side of ['R','L']){
   const thigh=pose.bones[bi('thigh'+side)],shin=pose.bones[bi('shin'+side)],foot=pose.bones[bi('foot'+side)];
   thigh.rot=qMul(qEuler(ease,0,0),thigh.rot);shin.rot=qMul(qEuler(-ease*2,0,0),shin.rot);
   const q=qMul(thigh.rot,shin.rot);foot.rot=[-q[0],-q[1],-q[2],q[3]];
  }
 }
 pose.rootOffset=superClip?[source.pos[B.root][2],0,source.pos[B.root][0]+dash]:mirror(source.pos[B.root]);
 const map=superClip?stage:mirror;
 const torso=Math.max(sk.bones[bi('pelvis')].radius*pose.bones[bi('pelvis')].thick,sk.bones[bi('chest')].radius*pose.bones[bi('chest')].thick);
 // A short arm cannot cross a broad chest to share a centreline handle. Fade
 // to an honest one-handed side grip, with the free arm counterbalancing.
 const sideGrip=isOriginalSkill(action)?smooth((torso/(length('upperArmR')+length('foreArmR'))-.68)/.14):0;
 const support=isOriginalSkill(action)?((events as SkillEvents).support??0)*(1-sideGrip):0;
 if(isOriginalSkill(action)&&(events as SkillEvents).groundWeapon&&sideGrip&&weapon==='hammer'){
  // Bow from the hip to lower the shoulder, rather than stretching a short
  // forearm through the belly. Counter-rotate the planted leg chains so their
  // authored foot positions and grounding remain unchanged.
  const weight=sideGrip*(events as SkillEvents).groundWeapon!,bend=weight*.25,bow=weight*.12;
  pose.bones[bi('pelvis')].rot=qMul(pose.bones[bi('pelvis')].rot,qEuler(bend,0,0));
  for(const side of ['R','L'])pose.bones[bi('thigh'+side)].rot=qMul(qEuler(-bend,0,0),pose.bones[bi('thigh'+side)].rot);
  pose.bones[bi('chest')].rot=qMul(pose.bones[bi('chest')].rot,qEuler(bow,0,0));
  pose.bones[bi('head')].rot=qMul(qEuler(-(bend+bow)*.6,0,0),pose.bones[bi('head')].rot);
 }
 function arm(src:SourcePose,side:'R'|'L',armed:boolean) {
  const sourceSide=side==='R'?'L':'R',up=src.rot[B[('upperArm'+sourceSide) as keyof typeof B]],fore=src.rot[B[('foreArm'+sourceSide) as keyof typeof B]];
  const ev=evaluate(sk,pose),shoulder=ev.joints[bi('upperArm'+side)],chest=ev.joints[bi('chest')];
  let displacement:V3,pole:V3;
  if(armed&&src.reachWeight>0){displacement=map(src.reach).map(v=>v*(length('upperArm'+side)+length('foreArm'+side))*src.reachExtent) as V3;pole=map(src.reachPole);}
  else {
   const upper=qRotate(qMul(src.rot[B.root],up),[0,-length('upperArm'+side),0]);
   const lower=qRotate(qMul(qMul(src.rot[B.root],up),fore),[0,-length('foreArm'+side),0]);
   displacement=map(upper.map((v,i)=>v+lower[i]) as V3);pole=map(qRotate(qMul(src.rot[B.root],up),[0,0,-1]));
  }
  let wrist=shoulder.map((v,i)=>v-chest[i]+displacement[i]) as V3;
  let shaft=armed?map(qRotate(src.aim,[0,1,0])):map(qRotate(qMul(qMul(src.rot[B.root],up),fore),[1,0,0]));
  if(armed&&support){
   const k=support,frame=ev.world[bi('chest')];
   const local=[0,1,2].map(i=>frame[i*3]*wrist[0]+frame[i*3+1]*wrist[1]+frame[i*3+2]*wrist[2]);
   const radius=Math.max(sk.bones[bi('pelvis')].radius*pose.bones[bi('pelvis')].thick,sk.bones[bi('chest')].radius*pose.bones[bi('chest')].thick);
   local[0]*=1-k;local[2]+=(Math.max(local[2],radius+.075)-local[2])*k;
   wrist=[0,1,2].map(i=>frame[i]*local[0]+frame[3+i]*local[1]+frame[6+i]*local[2]) as V3;
  }
  if(events.sheath&&(weapon==='sword'&&armed||weapon==='daggers')){
   // The source hides its inserted blade inside a separate scabbard. Atelier
   // has no scabbard attachment: use an outside carry as the exact sheath
   // track closes, rather than drive a fully visible blade through the coat.
   const radius=Math.max(sk.bones[bi('pelvis')].radius*pose.bones[bi('pelvis')].thick,sk.bones[bi('chest')].radius*pose.bones[bi('chest')].thick);
   const sign=side==='R'?-1:1,reachLength=length('upperArm'+side)+length('foreArm'+side),shoulderY=sk.bones[bi('upperArm'+side)].head[1]-sk.bones[bi('chest')].head[1];
   const carry=qRotate(root,[sign*(radius+.065),shoulderY-reachLength*.55,radius*.75]),point=qRotate(root,[sign*.25,-.75,.6]);
   const k=events.sheath;wrist=wrist.map((v,i)=>v+(carry[i]-v)*k) as V3;shaft=shaft.map((v,i)=>v+(point[i]-v)*k) as V3;
  }
  reach(side,wrist,shaft,pole);
 }
 arm(source,'R',true);
 if(weapon==='daggers'&&!superClip){
  const second=createPose();referenceStrike(second,Math.max(0,t-.275)/1.25,'sword');
  // A second dagger follows the same authored sweep reflected to the free arm.
  second.reach=mirror(second.reach);second.reachPole=mirror(second.reachPole);second.aim=mirrored(second.aim);
  arm(second,'L',true);
 }else if(isOriginalSkill(action)&&(events as SkillEvents).offhand){
  const second=createPose(),off=(events as SkillEvents).offhand!;second.reach=off.reach;second.reachExtent=off.extent;second.reachPole=off.pole;second.aim=off.aim;second.reachWeight=1;arm(second,'L',true);
 }else arm(source,'L',false);
 if(isOriginalSkill(action)){
  const skill=events as SkillEvents,grip=weaponGrip(sk,'R',weapon),hi=bi('handR');
  const point=(ev:ReturnType<typeof evaluate>,bone:number,p:V3):V3=>{const m=ev.skin,o=bone*16;return [0,1,2].map(i=>m[o+i]*p[0]+m[o+4+i]*p[1]+m[o+8+i]*p[2]+m[o+12+i]) as V3;};
  if(skill.groundWeapon){
   const prop=sk.byName.get('weaponR')??hi;
   if(action==='tempest'&&prop!==hi){
    // Slide the staff through the closed palm during a plant. The shorter
    // lower handle of a fixed central grip cannot reach past a broad tunic.
    // Only the rigid prop moves; the skin and both hand frames stay intact.
    const slide=(.14+(.27*grip.scale-.14)*sideGrip)*skill.groundWeapon;pose.bones[prop].offset=grip.across.map(v=>-v*slide) as V3;
   }
   const ev=evaluate(sk,pose),groundDelta=Math.min(...['R','L'].map(side=>{const i=bi('foot'+side);return boneTip(sk,pose,ev,i)[1]-sk.bones[i].tail[1];}));
   const center=point(ev,prop,grip.center),tip=point(ev,prop,grip.tip),shaft=normalize(tip.map((v,i)=>v-center[i]) as V3);
   const contact=weapon==='staff'?center.map((v,i)=>v-shaft[i]*.22*grip.scale):tip;
   const wrist=ev.joints[hi].map((v,i)=>v-ev.joints[bi('chest')][i]) as V3;
   const flank=weapon==='staff'?1:sideGrip;
   if(flank){
    const frame=ev.world[bi('chest')],local=[0,1,2].map(i=>frame[i*3]*wrist[0]+frame[i*3+1]*wrist[1]+frame[i*3+2]*wrist[2]);
    local[0]+=(Math.min(local[0],-torso-.045)-local[0])*flank*skill.groundWeapon;
    local[2]+=(.035-local[2])*flank*skill.groundWeapon;
    const next=[0,1,2].map(i=>frame[i]*local[0]+frame[3+i]*local[1]+frame[6+i]*local[2]);
    wrist[0]=next[0];wrist[2]=next[2];
    if(weapon==='staff'&&sideGrip){
     const outward=qRotate(root,[-1,0,0]),pelvis=ev.joints[bi('pelvis')],chest=ev.joints[bi('chest')];
     const current=outward.reduce((sum,v,i)=>sum+v*(wrist[i]+chest[i]-pelvis[i]),0),extra=Math.max(0,torso*1.32+.055-current)*sideGrip*skill.groundWeapon;
     for(let i=0;i<3;i++)wrist[i]+=outward[i]*extra;
    }
   }
   wrist[1]+=(groundDelta+(weapon==='hammer'?.052:.016)-contact[1])*skill.groundWeapon;
   // Reach downward first. Preserving a wide horizontal wind-up while simply
   // lowering Y clamps the whole vector and leaves a short staff floating.
   const shoulder=ev.joints[bi('upperArmR')].map((v,i)=>v-ev.joints[bi('chest')][i]),max=(length('upperArmR')+length('foreArmR'))*.955;
   const dy=Math.max(-max,Math.min(max,wrist[1]-shoulder[1])),horizontal=Math.sqrt(Math.max(0,max*max-dy*dy)),dx=wrist[0]-shoulder[0],dz=wrist[2]-shoulder[2],h=Math.hypot(dx,dz),k=Math.min(1,horizontal/Math.max(.0001,h));
   wrist[0]=shoulder[0]+dx*k;wrist[1]=shoulder[1]+dy;wrist[2]=shoulder[2]+dz*k;
   reach('R',wrist,shaft,stage(qRotate(source.rot[B.root],[1,-.4,-.5])));
  }
  if(support){
   // Resolve the free palm against the actual morphed weapon handle after the
   // primary arm and floor contact. No source fixed arm length can guarantee it.
   for(let pass=0;pass<2;pass++){
    const ev=evaluate(sk,pose),center=point(ev,hi,grip.center),tip=point(ev,hi,grip.tip),shaft=normalize(tip.map((v,i)=>v-center[i]) as V3);
    const left=weaponGrip(sk,'L',weapon),leftCenter=point(ev,left.bone,left.center),wrist=ev.joints[left.bone],chest=ev.joints[bi('chest')];
    const goal=wrist.map((v,i)=>v-chest[i]+(center[i]-shaft[i]*.055-leftCenter[i])*support) as V3;
    reach('L',goal,shaft,stage(qRotate(source.rot[B.root],[-1,-.3,-.5])));
   }
  }
 }
 // Exact source hover is the height of the pelvis, not an additive jump above
 // a dynamically grounded inverted pose. This preserves the full star path.
 if(superClip){
  const ev=evaluate(sk,pose),groundDelta=Math.min(...['R','L'].map(side=>{const i=bi('foot'+side);return boneTip(sk,pose,ev,i)[1]-sk.bones[i].tail[1];}));
  const groundedHip=sk.bones[bi('pelvis')].head[1]+pose.rootOffset[1]-groundDelta;
  return Math.max(0,(source.centre[1]-groundedHip)*source.hover+source.lift*(1-source.hover));
 }
 return 0;
}
